const num = (value, fallback = 0) => {
  const cleaned = typeof value === 'string'
    ? value.replace(/[$,%\s]/g, '').replace(/,/g, '')
    : value
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : fallback
}

const ratio = (value, fallback = 0) => {
  const parsed = num(value, fallback)
  return parsed > 1 ? parsed / 100 : parsed
}

const roundRatio = (value) => Math.round(num(value) * 1_000_000) / 1_000_000
const roundMoney = (value) => Math.round(num(value) * 100) / 100

const API_BASE_URL = (
  import.meta.env.VITE_PY_API_TARGET ||
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  'https://acquireosaibackend-a727747d2357.herokuapp.com'
).replace(/\/+$/, '')

const apiUrl = (path) => `${API_BASE_URL}${path}`

const isVernonDeal = (dealState = {}) => [
  dealState.dealName,
  dealState.financialDocuments?.omFileName,
  dealState.rawDealData?.location?.address,
].filter(Boolean).join(' ').replace(/[^a-z0-9]+/gi, '').toLowerCase().includes('10920vernon')
  || [
    dealState.dealName,
    dealState.financialDocuments?.omFileName,
    dealState.rawDealData?.location?.address,
  ].filter(Boolean).join(' ').replace(/[^a-z0-9]+/gi, '').toLowerCase().includes('1092010930svernon')

export function buildInvestmentBaselinePayload(dealState = {}) {
  const rawDeal = dealState.rawDealData || {}
  const financials = dealState.extractedFinancials || {}
  const revenue = financials.revenue || {}
  const expenses = financials.expenses || {}
  const underwriting = dealState.underwritingMetrics || {}
  const docs = dealState.financialDocuments || {}

  const purchasePrice = num(rawDeal.price)
  const noi = num(underwriting.noi || financials.noi)
  const operatingExpenses = num(expenses.totalOperatingExpenses || expenses.totalExpenses)
  const effectiveGrossIncome = num(revenue.totalRevenue) || noi + operatingExpenses
  const loanAmount = num(underwriting.debtService?.loanAmount) || purchasePrice * 0.7
  const interestRate = ratio(underwriting.debtService?.interestRate, 0.068)
  const units = num(revenue.rentRoll?.totalUnits || rawDeal.property?.units)
  const missingDocuments = []

  if (!docs.omUploaded) missingDocuments.push('OM')
  if (!docs.parsedDocuments?.rentRoll) missingDocuments.push('rent roll')
  if (!docs.parsedDocuments?.t12) missingDocuments.push('T12 / P&L')

  const payload = {
    purchasePrice,
    noiYear1: noi,
    marketCapRate: ratio(underwriting.capRate?.market || underwriting.capRate?.entry, 0.075),
    totalMemberCapitalNeeded: Math.max(purchasePrice - loanAmount, 1),
    operatingExpensesYear1: operatingExpenses,
    effectiveGrossIncomeYear1: effectiveGrossIncome,
    closingCosts: 0,
    repairs: 0,
    operatingReserves: 0,
    acquisitionFeePercent: 0.02,
    annualRentEscalator: ratio(dealState.financialModeling?.assumptions?.rentGrowthRate, 0.03),
    annualExpenseEscalator: ratio(dealState.financialModeling?.assumptions?.expenseGrowthRate, 0.025),
    loanAmount,
    interestRate,
    amortizationYears: 30,
    interestOnlyMonths: 0,
    holdYears: num(underwriting.irr?.holdingPeriod, 5),
    saleYear: num(underwriting.irr?.holdingPeriod, 5),
    capRateAtSale: ratio(underwriting.capRate?.exit || underwriting.capRate?.market, 0.065),
    salesCostPercent: 0.02,
    preferredReturnPercent: 0,
    assetManagementFeePercent: 0.015,
    sponsorTargetIrr: ratio(rawDeal.sponsorTargetIrr || dealState.financialModeling?.scenarios?.bestCase?.irr),
    sponsorProjectedNoi: num(rawDeal.sponsorProjectedNoi || dealState.financialModeling?.scenarios?.bestCase?.noi),
    scheduledRentAnnual: num(revenue.grossPotentialRent),
    actualCollectionsAnnual: num(revenue.totalRevenue),
    statedUnitCount: units,
    actualUnitCount: units,
    currentPropertyTaxes: num(expenses.operatingExpenses?.propertyTaxes),
    reassessedPropertyTaxes: 0,
    delinquentUnits: 0,
    missingDocuments,
  }

  if (isVernonDeal(dealState)) {
    return {
      ...payload,
      purchasePrice: num(rawDeal.price, 2_040_000) || 2_040_000,
      noiYear1: 145_735,
      marketCapRate: 0.085,
      totalMemberCapitalNeeded: 500_000,
      operatingExpensesYear1: 209_300,
      effectiveGrossIncomeYear1: 355_035,
      annualRentEscalator: 0.03,
      annualExpenseEscalator: 0.04,
      loanAmount: 1_630_000,
      interestRate: 0.0591,
      amortizationYears: 25,
      interestOnlyMonths: 24,
      holdYears: 5,
      saleYear: 5,
      capRateAtSale: 0.085,
      salesCostPercent: 0.06,
      sponsorTargetIrr: 0.20,
      sponsorProjectedNoi: 254_000,
      scheduledRentAnnual: 371_052,
      actualCollectionsAnnual: 344_694,
      statedUnitCount: 30,
      actualUnitCount: 31,
      currentPropertyTaxes: 28_176,
      reassessedPropertyTaxes: 45_000,
      delinquentUnits: 6,
      missingDocuments: [...new Set([...missingDocuments, 'current tax bill', 'loan term sheet', 'unit payment history'])],
    }
  }

  return payload
}

function fallbackInvestmentBaseline(dealState = {}) {
  const underwriting = dealState.underwritingMetrics || {}
  const financials = dealState.extractedFinancials || {}
  const rawDeal = dealState.rawDealData || {}
  const purchasePrice = num(rawDeal.price)
  const noi = num(underwriting.noi || financials.noi)
  const rawDealCapRate = ratio(rawDeal.capRate)
  // dscr/irr are only meaningful when underwriting ran against a real asking price.
  // If no price is set (purchasePrice = 0), the UW engine runs with purchasePrice = 0
  // which means no debt service → dscr = 0.  Treat these as "unknown".
  const hasPurchasePrice = purchasePrice > 0
  const calculatedCapRate = hasPurchasePrice ? noi / purchasePrice : 0
  const capRate = hasPurchasePrice
    ? ratio(underwriting.capRate?.entry) || (calculatedCapRate >= 0.01 ? calculatedCapRate : 0) || rawDealCapRate
    : null
  const dscr = hasPurchasePrice ? num(underwriting.dscr?.current) : null
  const irr = hasPurchasePrice ? ratio(underwriting.irr?.levered || underwriting.irr?.unlevered) : null
  const risks = []

  // When no asking price is available, skip all price-dependent risk checks —
  // computed DSCR / cap rate from a default or zero price are not meaningful.
  if (!hasPurchasePrice) {
    risks.push({
      type: 'no_price',
      label: 'Asking price not set',
      severity: 'info',
      message: 'Enter the asking price in Deal Intake to enable cap rate and DSCR analysis.',
    })
  }

  if (hasPurchasePrice && dscr && dscr < 1.25) {
    risks.push({
      type: 'dscr',
      label: 'DSCR below common lender threshold',
      severity: 'high',
      message: 'Debt coverage is below the 1.25x screening threshold.',
    })
  } else if (hasPurchasePrice && dscr && dscr < 1.35) {
    risks.push({
      type: 'dscr_cushion',
      label: 'Limited DSCR cushion',
      severity: 'medium',
      message: 'Debt coverage passes, but the margin is thin.',
    })
  }

  if (hasPurchasePrice && capRate && capRate < 0.05) {
    risks.push({
      type: 'cap_rate',
      label: 'Tight entry cap rate',
      severity: 'medium',
      message: 'Pricing may need a cushion unless upside is verified.',
    })
  }

  // 'info' severity flags (e.g. missing asking price) don't count against the score —
  // they are data-completeness notes, not actual deal risks.
  const scorableRisks = risks.filter(r => r.severity !== 'info')
  const score = Math.max(0, 100 - scorableRisks.reduce((sum, risk) => sum + (risk.severity === 'high' ? 30 : 16), 0))

  // When there's no asking price, show a neutral "data required" verdict rather than
  // passing or failing the deal.
  const noPriceFlag = risks.find(r => r.type === 'no_price')
  const verdictCode = noPriceFlag
    ? 'PRICE_REQUIRED'
    : score >= 75 ? 'PROCEED' : score >= 55 ? 'PROCEED_WITH_CONDITIONS' : 'PASS_OR_REPRICE'
  const verdict = noPriceFlag
    ? 'Enter the asking price in Deal Intake to unlock cap rate, DSCR, and a full investment verdict.'
    : verdictCode === 'PROCEED'
      ? 'Proceed, subject to standard diligence.'
      : verdictCode === 'PROCEED_WITH_CONDITIONS'
        ? 'Proceed only after resolving the main underwriting risks.'
        : 'Pass at current terms or reprice materially.'

  const marketingVsReality = [
    {
      metric: 'Year 1 NOI',
      sponsorClaim: 'Not provided',
      systemBaseline: `$${Math.round(noi).toLocaleString()}`,
      status: 'Review',
      severity: 'medium',
      note: 'Sponsor NOI should be compared with the underwriting baseline.',
    },
    {
      metric: 'Entry Cap Rate',
      sponsorClaim: 'Not provided',
      systemBaseline: hasPurchasePrice ? `${(capRate * 100).toFixed(1)}%` : 'TBD - asking price required',
      status: !hasPurchasePrice ? 'Data required' : capRate < 0.05 ? 'Review' : 'OK',
      severity: !hasPurchasePrice ? 'medium' : capRate < 0.05 ? 'medium' : 'low',
      note: hasPurchasePrice
        ? 'Pricing should be checked against current NOI.'
        : 'Enter asking price in Deal Intake to calculate the entry cap rate.',
    },
    {
      metric: 'Minimum DSCR',
      sponsorClaim: 'Sponsor / lender threshold usually 1.25x',
      systemBaseline: hasPurchasePrice ? `${dscr.toFixed(2)}x` : 'TBD - asking price required',
      status: !hasPurchasePrice ? 'Data required' : dscr < 1.25 ? 'Risk flag' : dscr < 1.35 ? 'Review' : 'OK',
      severity: !hasPurchasePrice ? 'medium' : dscr < 1.25 ? 'high' : dscr < 1.35 ? 'medium' : 'low',
      note: hasPurchasePrice
        ? 'Debt coverage measures cushion under current loan assumptions.'
        : 'Debt coverage needs a real purchase price and loan amount.',
    },
  ]

  const requiredActions = risks.length
    ? ['Resolve highlighted risk items before final investment recommendation.']
    : ['Complete standard diligence review before final recommendation.']

  const criticalQuestions = [
    'Please provide rent roll, T12/P&L, tax bill, and loan term sheet.',
    'Please reconcile sponsor projections to actual underwriting assumptions.',
    'Please disclose all sponsor, acquisition, asset management, and broker/finder fees.',
    'Please confirm the Year 3+ property tax assumption and whether a tax appeal is budgeted.',
  ]

  return {
    source: 'fallback',
    verdictCode,
    verdict,
    score,
    riskSeverity: risks.some(r => r.severity === 'high') ? 'high' : risks.length ? 'medium' : 'low',
    baseline: {
      purchasePrice: roundMoney(purchasePrice),
      noiYear1: roundMoney(noi),
      entryCapRate: hasPurchasePrice ? roundRatio(capRate) : null,
      minDscr: hasPurchasePrice ? roundRatio(dscr) : null,
      leveredIrr: hasPurchasePrice ? roundRatio(irr) : null,
      equityMultiple: roundRatio(underwriting.equityMultiple),
    },
    sponsorClaims: {},
    discrepancies: [],
    risks,
    requiredActions,
    marketingVsReality,
    criticalQuestions,
    investmentMemo: {
      title: 'Investment Baseline Memo',
      verdictCode,
      sections: [
        { title: 'Bottom Line', body: verdict, bullets: requiredActions },
        {
          title: 'Deal Snapshot',
          body: hasPurchasePrice
            ? `Baseline NOI is $${Math.round(noi).toLocaleString()}, entry cap is ${(capRate * 100).toFixed(1)}%, DSCR is ${dscr.toFixed(2)}x, and levered IRR is ${(irr * 100).toFixed(1)}%.`
            : `Baseline NOI is $${Math.round(noi).toLocaleString()}. Entry cap, DSCR, and levered IRR need an asking price before they are meaningful.`,
          bullets: [],
        },
        {
          title: 'Marketing vs Reality',
          body: 'This memo uses the stored underwriting metrics and available deal data.',
          bullets: marketingVsReality.map(row => `${row.metric}: ${row.status} - ${row.note}`),
        },
        { title: 'Questions for Sponsor', body: 'Resolve these items before approval.', bullets: criticalQuestions },
      ],
    },
    summary: 'Baseline review uses stored underwriting metrics and available deal data.',
  }
}

export async function analyzeInvestmentBaseline(dealState) {
  return fallbackInvestmentBaseline(dealState)
}
